import { Link } from "react-router-dom";
import { ArrowRight, CircuitBoard, Cpu, Database, Gpu, HardDrive, Keyboard, MemoryStick, Send } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import CyberBackground from "@/components/CyberBackground";

const PRODUCTS = [
  { icon: MemoryStick, name: "RAM & Memory", desc: "DDR4 and DDR5 modules for laptops and desktops, from 8GB to 64GB kits.", slug: "ram-memory" },
  { icon: HardDrive, name: "SSD Storage", desc: "Fast NVMe and SATA solid-state drives from 250GB up to 4TB.", slug: "ssd-storage" },
  { icon: Database, name: "Hard Drives", desc: "Desktop, portable and NAS hard drives from 1TB to 16TB.", slug: "hard-drives" },
  { icon: Gpu, name: "Graphics Cards", desc: "GPUs for design work, video editing and CAD workstations.", slug: "graphics-cards" },
  { icon: Cpu, name: "Processors", desc: "Intel and AMD CPUs for upgrades and full custom builds.", slug: "processors" },
  { icon: Keyboard, name: "Peripherals & Accessories", desc: "Keyboards, mice, docking stations, cables and adapters.", slug: "peripherals" },
  { icon: CircuitBoard, name: "Components & Parts", desc: "Motherboards, power supplies, cases and cooling.", slug: "components" },
];

export default function Products() {
  return (
    <div data-testid="products-page">
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40">
        <CyberBackground />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-300" data-testid="products-on-demand-badge">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
              </span>
              Sourced on demand
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="products-page-title">
              IT hardware, <span className="text-gradient">sourced on demand</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Tell us what you need and we'll source it for you — quality components and
              peripherals, supplied and fitted by the team you already trust. No catalogue
              browsing, no guesswork: send an enquiry and we'll come back with options and a price.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="What we source"
          title="Hardware we can get for you"
          description="Every item is hand-picked for your needs, tested and, where you need it, installed and configured by our own team."
          testid="products-catalogue"
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.07}>
              <article className="card-glow group relative flex h-full flex-col rounded-2xl border border-white/10 bg-navy-900/60 p-6" data-testid={`product-card-${p.slug}`}>
                <span className="absolute right-4 top-4 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-cyan-300" data-testid={`product-badge-${p.slug}`}>
                  On demand
                </span>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5 transition-colors group-hover:bg-cyan-400/15">
                  <p.icon className="h-5 w-5 text-cyan-300" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">{p.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                <Link
                  to={`/contact?service=${encodeURIComponent("Hardware / Products")}&message=${encodeURIComponent(`Hardware enquiry — I'm interested in: ${p.name}. Please get back to me with options and pricing.`)}`}
                  className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:text-cyan-300"
                  data-testid={`product-enquiry-${p.slug}`}
                >
                  <Send className="h-3.5 w-3.5" /> Send enquiry
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="relative mx-auto max-w-4xl overflow-hidden px-4 py-20 text-center sm:px-6">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,102,255,0.15),transparent_65%)]" />
          <Reveal className="relative">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">Need something not listed here?</h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
              If it's IT hardware, we can almost certainly source it. Tell us what you're looking
              for and we'll find it, price it and deliver it.
            </p>
            <Link
              to="/contact?service=Hardware%20%2F%20Products&message=Hardware%20enquiry%20—%20I'm%20looking%20for:%20"
              data-testid="products-contact-button"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_35px_rgba(0,102,255,0.4)] transition-all hover:bg-cyan-400 hover:text-ink"
            >
              Send an enquiry <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
